// src/components/agenda/LivePollModal.js
import React, { useState, useEffect } from 'react';
import { db, functions } from '../../services/firebase'; // Pfad anpassen
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Dieselben Imports wie in PollDisplay, aber als MODAL
import PollDisplay from './PollDisplay';

// Helper für Cloud Function-Aufrufe
const castVoteFunc = httpsCallable(functions, 'castVote');

/**
 * Diese Komponente hört auf alle 'offenen' Umfragen für ein bestimmtes Meeting
 * und zeigt die erste offene Umfrage als modales Popup an.
 */
const LivePollModal = ({ companyId, meetingId, currentUserId, totalParticipants, setNotification }) => {
    const [activePoll, setActivePoll] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!companyId || !meetingId || !currentUserId) {
            setIsLoading(false);
            return;
        }

        const pollsColRef = collection(db, `companies/${companyId}/meetings/${meetingId}/polls`);
        
        // Query: Finde alle Umfragen, die 'open' sind
        const q = query(
            pollsColRef,
            where("status", "==", "open")
        );

        setIsLoading(true);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.empty) {
                // Wenn keine Umfrage offen ist, schließe das Modal
                setActivePoll(null);
                setIsLoading(false);
                return;
            }
            
            // Nimm die erste offene Umfrage
            const firstOpenPollDoc = snapshot.docs[0];
            const pollData = firstOpenPollDoc.data();
            
            setActivePoll({
                ...pollData,
                id: firstOpenPollDoc.id,
            });
            setIsLoading(false);

        }, (error) => {
            console.error("Error listening to active polls (LivePollModal): ", error);
            // Dieser Fehler tritt auf, wenn firestore.rules (list) fehlschlägt
            setNotification({ message: 'Fehler: Live-Umfrage-Listener fehlgeschlagen.', type: 'error' });
            setIsLoading(false);
        });

        return () => unsubscribe();

    }, [companyId, meetingId, currentUserId, setNotification]);


    // === Teilnehmer-Abstimmungslogik (vereinfacht aus PollDisplay) ===
    
    const [hasVoted, setHasVoted] = useState(null);
    const [isVoting, setIsVoting] = useState(false);

    // Prüfen, ob der User schon für DIESE (activePoll) Umfrage abgestimmt hat
    useEffect(() => {
        if (!activePoll || !currentUserId) {
            setHasVoted(null);
            return;
        }

        const voteDocRef = doc(db, `companies/${companyId}/meetings/${meetingId}/polls/${activePoll.id}/votes/${currentUserId}`);
        const unsubscribe = onSnapshot(voteDocRef, (doc) => {
            setHasVoted(doc.exists());
        });
        return () => unsubscribe();
    }, [activePoll, currentUserId, companyId, meetingId]);

    const handleVote = async (optionId) => {
        setIsVoting(true);
        try {
            await castVoteFunc({ companyId, meetingId, pollId: activePoll.id, optionId });
            setNotification({ message: 'Stimme erfolgreich abgegeben!', type: 'success' });
            // 'hasVoted' wird durch den Listener aktualisiert
        } catch (error) {
             console.error("Error casting vote:", error);
             setNotification({ message: `Abstimmen fehlgeschlagen: ${error.message}`, type: 'error' });
        }
        setIsVoting(false);
    };


    // === Render-Logik ===

    if (isLoading || !activePoll) {
        return null; // Wenn geladen wird oder keine Umfrage aktiv ist -> kein Popup
    }

    return (
        // Overlay (Hintergrund)
        <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
        >
            {/* Modal-Container */}
            <div className="relative z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-gray-200 dark:border-gray-700">
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Live-Umfrage
                </h2>

                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{activePoll.question}</h3>
                    
                    {/* Ansicht 1: Bereits abgestimmt */}
                    {hasVoted === true && (
                         <div className="text-center p-4 bg-green-100 dark:bg-green-900 rounded-lg mt-2">
                            <span className="font-semibold text-green-800 dark:text-green-200">
                                Vielen Dank für Ihre Teilnahme.
                            </span>
                            <p className="text-sm text-green-700 dark:text-green-300">
                                Die Ergebnisse werden nach Beendigung der Umfrage im Protokoll angezeigt.
                            </p>
                        </div>
                    )}

                    {/* Ansicht 2: Noch nicht abgestimmt */}
                    {hasVoted === false && (
                        <div className="space-y-2 mt-2">
                            <h5 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Bitte abstimmen:</h5>
                            {activePoll.options.map(option => (
                                <button
                                    key={option.id}
                                    onClick={() => handleVote(option.id)}
                                    disabled={isVoting}
                                    className="w-full text-left px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50"
                                >
                                    {option.text}
                                </button>
                            ))}
                            {isVoting && <p className="text-sm text-center text-gray-500">Stimme wird übermittelt...</p>}
                        </div>
                    )}

                    {/* Ansicht 3: Lade Abstimmungsstatus */}
                    {hasVoted === null && (
                        <div className="text-center p-4 text-gray-500">Prüfe Abstimmungsstatus...</div>
                    )}
                </div>
                
                {/* Hinweis: Es gibt keinen "Schließen"-Button. Das Modal verschwindet, 
                    sobald der Moderator die Umfrage (Status 'open') beendet. */}
            </div>
        </div>
    );
};

export default LivePollModal;

